import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {Spin} from 'com/gongxigames/math/core/game/Spin';
import {TotalBet} from 'com/gongxigames/math/core/game/TotalBet';
import {LinesSlotEngine} from 'com/gongxigames/math/core/game/engine/LinesSlotEngine';
import {WaysSlotEngine} from 'com/gongxigames/math/core/game/engine/WaysSlotEngine';
import {ArrayUtils} from 'com/gongxigames/math/core/util/ArrayUtils';
import {SlotSymbol} from 'com/gongxigames/math/core/game/engine/SlotSymbol';
import {GameClientStub} from 'com/gongxigames/math/core/client/GameClientStub';
import {ReelSet} from 'com/gongxigames/math/core/game/ReelSet';
import {Bonus} from 'com/gongxigames/math/core/game/engine/bonus/Bonus';
import {SelectBonus} from 'com/gongxigames/math/core/game/engine/bonus/SelectBonus';
import {FreeGamesBonus} from 'com/gongxigames/math/core/game/engine/bonus/FreeGamesBonus';
import {PickBonus} from 'com/gongxigames/math/core/game/engine/bonus/PickBonus';
import {GameClientProxy} from 'com/gongxigames/math/core/client/GameClientProxy';
import {IEventManagerWithAsync} from '@gongxigames/framework/core/types/IEventManager';
import {Session} from 'com/gongxigames/math/core/game/session/Session';
import {SlotEngine} from 'com/gongxigames/math/core/game/SlotEngine';
import {ResponseFilter} from 'com/gongxigames/math/core/client/ResponseFilter';
import {GameHistoryEntry} from 'com/gongxigames/math/core/client/history/GameHistoryEntry';
import {FirecatcherRespinBonus} from 'com/gongxigames/math/core/game/engine/bonus/firecatcher/FirecatcherRespinBonus';
import {Message} from 'com/gongxigames/math/core/game/message/Message';
import {Field} from 'com/gongxigames/math/core/game/message/Field';
import {FirecatcherSpin} from 'com/gongxigames/math/core/game/FirecatcherSpin';
import {Line} from 'com/gongxigames/math/core/game/Line';
import {
    consoleLogger,
    CoreDI,
    IBrowser,
    ICurrencyFormatter,
    inject,
    injectable,
    IService, storage,
    Installer, SystemComponentType, Timer,
    UpdateManager
} from '@gongxigames/framework/core';
import {BetInfo, ILogic, RoundInfo} from '@gongxigames/framework/slot/types';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {SlotSettings} from '@gongxigames/framework/slot/configs/SlotSettings';
import {ILogicBuyFeatureFunctional} from '@gongxigames/framework/slot/services/logic/buyFeature/ILogicBuyFeatureFunctional';
import {IBuyFeatureLogic} from '@gongxigames/framework/slot/services/logic/buyFeature/IBuyFeatureLogic';
import {GameClientWrapper} from '@gongxigames/framework/slot/services/logic/GameClientWrapper';
import {LogicEvents} from '@gongxigames/framework/slot/services/logic/LogicEvents';
import {ServerMessage} from '@gongxigames/framework/slot/services/logic/ServerMessage';
import {BuyFeatureLogicWrapper, BuyFeatureType} from '@gongxigames/framework/slot/services/logic/buyFeature/BuyFeatureLogicWrapper';
import {GameClientWrapperFactory} from '@gongxigames/framework/integrations/GameClientWrapperFactory';
import {slotStore} from '@gongxigames/framework/slotV2/globals';
import {Pay} from 'com/gongxigames/math/core/game/Pay';
import {PayType} from 'com/gongxigames/math/core/game/enums/PayType';
import {TumbleSpin, PayEntries} from '@gongxigames/framework/slotV2/types/LogicData';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals';
import {createPromotionLogic, IPromotionLogic, ILogicPromotionFunctional, PromotionQualifier} from '@gongxigames/framework/promotions';

@injectable()
export class Logic<GenericSession extends Session, GenericEngine extends SlotEngine<GenericSession>, BuyFeatureT = BuyFeatureType> implements
    ILogic, ILogicPromotionFunctional, ILogicBuyFeatureFunctional<BuyFeatureT>, IService {
    public type: SystemComponentType = 'service';
    public static readonly NAME = DI.Logic;

    protected _totalBetSymbolIndexes: number[] = [];
    protected _wager = 0;
    protected _betId = 0;
    protected _previousAction: Action = Action.SPIN;
    protected _client!: GameClientProxy<GenericSession, GenericEngine> | GameClientStub<GenericSession, GenericEngine>;

    protected _clientWrapper!: GameClientWrapper<GenericSession, GenericEngine>;
    protected _spin!: Spin;
    protected _previousSpinRoundWin = 0;
    protected _callback?: Function;
    protected _retriggerCount = 0;
    protected _hyperSpin!: number[];
    protected _nextAction: Action = Action.SPIN;

    @inject(CoreDI.IEventManagerWithAsync)
    protected _eventManager!: IEventManagerWithAsync;

    @inject(CoreDI.UpdateManager)
    protected _updateManager!: UpdateManager;

    @inject(CoreDI.Browser)
    protected _browser!: IBrowser;

    @inject(CoreDI.CurrencyFormatter)
    protected _currencyFormatter!: ICurrencyFormatter;

    @inject(CoreDI.Settings)
    protected settings!: SlotSettings;

    @inject(CoreDI.Installer)
    protected _installer!: Installer;

    protected _promotionLogic!: IPromotionLogic;
    protected _buyFeatureLogic!: IBuyFeatureLogic<BuyFeatureT>;

    public async init(): Promise<void> {
        this._client = this.createGameClientProxy(this.settings.get('offlineMode'));

        const wrapperFactory = this._installer.getComponent<GameClientWrapperFactory>(GameClientWrapperFactory.NAME);
        const clientWrapperProperties = {
            callback: this.onResponse.bind(this),
            client: this._client
        };

        this._clientWrapper = wrapperFactory.create();
        this._clientWrapper.initWrapper(clientWrapperProperties);
        this.createPromotionLogic();
        this.createBuyFeatureLogicWrapper();
    }

    public async initialize(): Promise<void> {
        await this.initAction();
    }

    private async initAction(): Promise<void> {
        return new Promise(resolve => {
            this._callback = resolve;

            this._updateManager.add(new Timer(() => {
                this._clientWrapper.init();
            }, 0, true));
        });
    }

    public getUpgradeIndex(): number {
        return (this._spin as FirecatcherSpin).upgradeIndex;
    }

    public getBonus<T extends Bonus>(bonusID: number): T {
        return this._client.getSession().getBonus<T>(bonusID);
    }

    public getFirecatcherPrizes(): number[] {
        const bonus: FirecatcherRespinBonus = this.getFirecatcherBonus();

        return bonus ? bonus.getPrizes() : [];
    }

    public getFirecatcherPrizeCount(): number {
        const bonus: FirecatcherRespinBonus = this.getFirecatcherBonus();

        return bonus && bonus.getPrizeCount();
    }

    public getFirecatcherRespinCount(): number {
        const bonus: FirecatcherRespinBonus = this.getFirecatcherBonus();

        return bonus ? bonus.getCount() : 0;
    }

    public getResultsOfHiddenWins(): number[][] {
        const bonus = this.getPickBonus();

        const hasHiddenWinsMethod = typeof (bonus as any).getResultsOfHiddenWins === 'function';

        if (hasHiddenWinsMethod) {
            return (bonus as any).getResultsOfHiddenWins();
        }

        const indexes: number[] = [];
        const jackpotIds: number[] = [];
        const multipliers: number[] = [];

        return [indexes, jackpotIds, multipliers];
    }
    public getJackpotPayments(): number[] {
        const bonus = this.getPickBonus();

        const hasMethod = typeof (bonus as any).getJackpotPayments === 'function';

        if (hasMethod) {
            // NOTE please be sure that your math file include getJackpotPayments
            return (bonus as any).getJackpotPayments();
        }

        return [];
    }

    private createPromotionLogic(): void {
        this._promotionLogic = createPromotionLogic(this._clientWrapper.getPromotion(), this);
    }

    public updateSessionBet(): void {
        this._wager = this._client.getEngine().getMaxWager();
        this._client.getSession().setTotalBet(new TotalBet(this.getBet(), this._wager));
    }

    public isWaysGame(): boolean {
        return this._client.getEngine() instanceof WaysSlotEngine;
    }

    public isLinesGame(): boolean {
        return this._client.getEngine() instanceof LinesSlotEngine;
    }

    public isBigWin(): boolean {
        return this.getWin() >= this.getBigWinLimit();
    }

    public isBigTotalWin(): boolean {
        return this.getTotalWin() >= this.getBigTotalWinLimit();
    }

    public getBigWinLimit(): number {
        let threshold = slotStore.big_win_threshold.value;

        if (this.isSpecialWinConfigBuyFeature()) {
            // as we have a lot of condition where getBigWinLimit() is using with >= sign;
            // but SpecialWinConfigBuyFeature requirement declare strict > sign for this.getBigWinLimit()
            // added this epsilon which 10x lower than the smallest possible win and in such case
            // part this.getWin() == getBigWinLimit() never happens
            const epsilon = 0.1 / this._currencyFormatter.fractionSize;

            threshold = Math.max(threshold, this.getBuyFeatureMultiplier() + epsilon);
        }

        return this.getTotalBet() * threshold;
    }

    protected getBigTotalWinLimit(): number {
        let limit = this.getBigWinLimit();

        if (this.isBuyFeatureInProgress()) {
            limit = Math.max(limit, this.getBuyFeaturePrice());
        }

        return limit;
    }

    public isSpecialWinConfigBuyFeature(): boolean {
        return Boolean(this.settings.get('specialWinTypeConfig') && this.isBuyFeatureInProgress()
            && this.getTotalWin() <= this.getBuyFeaturePrice());
    }

    protected isBuyFeatureInProgress(): boolean {
        return this.getBuyFeature().isBuyFeatureGame() && this.getBuyFeature().isInProgress(this.getBuyFeature().getType());
    }

    public nextActionIsSpin(): boolean {
        return this.nextAction === Action.SPIN;
    }

    public getMultiPickValue(): number {
        const bonus = this.getPickBonus();

        return bonus.getPickedValues()[bonus.getPickedIndices().length - 1];
    }

    public getMathVersion(): number {
        return this._client.getEngine().getMathVersion();
    }

    // TODO: move to utils
    public removeFromArray(src: number[], toRemove: number[]): void {
        for (const element of toRemove) {
            const index = ArrayUtils.indexOf(src, element);

            if (index >= 0) {
                ArrayUtils.remove(src, index);
            }
        }
    }

    public getSlotSymbol(symbol: number): SlotSymbol {
        return this._client.getEngine().getSlotSymbol(symbol);
    }

    public getNotPickedValues(): number[][] | null {
        const bonus = this.getPickBonus();

        if (!bonus) {
            return null;
        }

        const notPickedIndices: number[] = this.createArray(bonus.getPickedValues().length);

        this.removeFromArray(notPickedIndices, bonus.getPickedIndices());

        const res: number[][] = [];

        for (let i = 0; i < notPickedIndices.length; i++) {
            res[i] = [];
            res[i][0] = notPickedIndices[i];
            res[i][1] = bonus.getPickedValues()[i + bonus.getPickedIndices().length];
        }

        return res;
    }

    public getPickedValues(): number[][] | null {
        const bonus = this.getPickBonus();

        if (!bonus) {
            return null;
        }

        const res: number[][] = [];

        for (let i = 0; i < bonus.getPickedIndices().length; i++) {
            res[i] = [];
            res[i][0] = bonus.getPickedIndices()[i];
            res[i][1] = bonus.getPickedValues()[i];
        }

        return res;
    }

    public getSessionForFirecatcherWinningRound(): Spin {
        const firecatcherActionNumber = this._client.getSession().getBonus(Bonus.RESPIN_BONUS).getActionNumber();
        const historyEntry: GameHistoryEntry = this._client.getGameHistory().getEntry(firecatcherActionNumber);

        return historyEntry.getSpin(this._client.getEngine());
    }

    public getSessionForFreeSpinsWinningRound(): Spin {
        const historyEntry: GameHistoryEntry = this._client.getGameHistory().getEntry(this.getFreeGamesBonusActionNumber());

        return historyEntry.getSpin(this._client.getEngine());
    }

    public getSessionForRespinWinningRound(): Spin {
        const historyEntry: GameHistoryEntry = this._client.getGameHistory().getEntry(this.getRespinActionNumber());

        return historyEntry.getSpin(this._client.getEngine());
    }

    public getSessionForMiniSpinWinningRound(): Spin {
        const historyEntry: GameHistoryEntry = this._client.getGameHistory().getEntry(this.getMiniSpinActionNumber());

        return historyEntry.getSpin(this._client.getEngine());
    }

    protected getFreeGamesBonusActionNumber(): number {
        let freeGamesBonusActionNumber;

        // check cases for last free spin (it can be with any bonus)
        if (this.getCurrentBonus(Bonus.FREE_GAMES_BONUS)) {
            freeGamesBonusActionNumber = this.getCurrentBonus(Bonus.FREE_GAMES_BONUS).getActionNumber();
        } else if (this.getCurrentBonus(Bonus.PICK_BONUS)) {
            freeGamesBonusActionNumber = this.getParentBonusActionNumber(Bonus.FREE_GAMES_BONUS, Bonus.PICK_BONUS);
        } else if (this.getCurrentBonus(Bonus.RESPIN_BONUS)) {
            freeGamesBonusActionNumber = this.getParentBonusActionNumber(Bonus.FREE_GAMES_BONUS, Bonus.RESPIN_BONUS);
        } else if (this.getCurrentBonus(Bonus.SELECT_BONUS)) {
            freeGamesBonusActionNumber = this.getParentBonusActionNumber(Bonus.FREE_GAMES_BONUS, Bonus.SELECT_BONUS);
        }

        return freeGamesBonusActionNumber;
    }

    protected getCurrentBonus(bonusType: number): any {
        return this._client.getSession().getBonus(bonusType);
    }

    protected getParentBonusActionNumber(parentBonusId: number, currentBonusId: number): number {
        const bonusActionNumber = this.getCurrentBonus(currentBonusId).getActionNumber();
        const historyEntry = this._client.getGameHistory().getEntry(bonusActionNumber);

        return historyEntry.getSessionBefore().getBonus(parentBonusId).getActionNumber();
    }

    public isBonusOnFreeGames(bonusId?: number): boolean {
        const bonus = bonusId ? bonusId : this._spin.bonusId;
        const bonusActionNumber = this.getCurrentBonus(bonus).getActionNumber();
        const historyEntry = this._client.getGameHistory().getEntry(bonusActionNumber);

        return !!historyEntry.getSessionBefore().getBonus(Bonus.FREE_GAMES_BONUS);
    }

    public getPreviousRoundSession(): Spin {
        const previousActionNumber = this.getActionNumber() - 1;
        const engine = this._client.getEngine();
        const historyEntry = this._client.getGameHistory().getEntry(previousActionNumber);

        return historyEntry.getSpin(engine);
    }

    protected getRespinActionNumber(): number {
        return this.getCurrentBonus(Bonus.RESPIN_BONUS).getActionNumber();
    }

    protected getMiniSpinActionNumber(): number {
        // TODO: refactoring required Bonus.MINI_GAME_BONUS not exists
        // @ts-ignore
        return this.getCurrentBonus(Bonus.MINI_GAME_BONUS).getActionNumber();
    }

    public isRetriggerWon(): boolean {
        const retriggerCount = this.getRetriggerCount();

        return this._spin.bonusId === Bonus.FREE_GAMES_BONUS && retriggerCount > 0;
    }

    public isFreeSpinsWon(): boolean {
        const retriggerCount = this.getRetriggerCount();

        return this._spin.bonusId === Bonus.FREE_GAMES_BONUS && retriggerCount <= 0;
    }

    public isLastFreeSpin(): boolean {
        const totalRestored = this.getRestoredFreeGamesTotalCount();
        const total = this.getFreeGamesTotalCount();

        return this.getFreeGamesCount() === 0 && (totalRestored > 0 || total > 0);
    };

    public getRetriggerCount(): number {
        return this._retriggerCount;
    }

    public setFilter(filter: ResponseFilter<Session>): void {
        this._client.setResponseFilter(filter);
    }

    public setFilterForStopPositions(randomValues: number[] | null): void {
        this._client.setRandomValues(randomValues!);
    }

    public getPickId(): number {
        return this.getSelectBonus().getPickValue();
    }

    public getPicks(): number[] {
        return this.getSelectBonus().getValues();
    }

    public getPickGameWin(): number {
        return this.getPickBonus().getWin();
    }

    public getBets(): number[] {
        return this._clientWrapper.getBets();
    }

    public getDefaultBet(): number {
        return this._clientWrapper.getDefaultBet();
    }

    public getFreeGamesTotalCount(): number {
        const bonus = this.getFreeGamesBonus();

        if (!bonus) {
            return 0;
        }

        return bonus.getCount();
    }

    public getFreeGamesCount(): number {
        const bonus = this.getFreeGamesBonus();

        if (!bonus) {
            return 0;
        }

        return bonus.getCount() - bonus.getIndex();
    }

    public getFreeGamesIndex(): number {
        const bonus = this.getFreeGamesBonus();

        if (!bonus) {
            return -1;
        }

        return bonus.getIndex();
    }

    public getBonusWin(bonusId: number): number {
        const bonus = this.getBonus(bonusId);

        if (!bonus) {
            return 0;
        }

        return bonus.getWin();
    }

    public getLines(): Line[] | null {
        const engine = this._client.getEngine();

        if (engine instanceof LinesSlotEngine) {
            return engine.getLines();
        }

        return null;
    }

    public getRestoredFreeGamesTotalCount(): number {
        const bonus: FreeGamesBonus = this.getFreeGamesBonusForRestore();

        return bonus?.getCount() ?? 0;
    }

    protected getFreeGamesBonusForRestore(): FreeGamesBonus {
        let freeGamesBonus;

        if (this.getCurrentBonus(Bonus.FREE_GAMES_BONUS)) {
            freeGamesBonus = this.getCurrentBonus(Bonus.FREE_GAMES_BONUS);
        } else if (this.getCurrentBonus(Bonus.PICK_BONUS)) {
            freeGamesBonus = this.getParentFreeGamesBonus(Bonus.FREE_GAMES_BONUS, Bonus.PICK_BONUS);
        } else if (this.getCurrentBonus(Bonus.RESPIN_BONUS)) {
            freeGamesBonus = this.getParentFreeGamesBonus(Bonus.FREE_GAMES_BONUS, Bonus.RESPIN_BONUS);
        } else if (this.getCurrentBonus(Bonus.SELECT_BONUS)) {
            freeGamesBonus = this.getParentFreeGamesBonus(Bonus.FREE_GAMES_BONUS, Bonus.SELECT_BONUS);
        }

        return freeGamesBonus;
    }

    protected getParentFreeGamesBonus(parentBonusId: number, currentBonusId: number): FreeGamesBonus {
        const bonusActionNumber = this.getCurrentBonus(currentBonusId).getActionNumber();
        const historyEntry = this._client.getGameHistory().getEntry(bonusActionNumber);

        return historyEntry.getSessionBefore().getBonus(parentBonusId);
    }

    public getBaseWinFromTriggeringRound(currentBonusId: number): number {
        const bonusActionNumber = this.getCurrentBonus(currentBonusId).getActionNumber();
        const historyEntry = this._client.getGameHistory().getEntry(bonusActionNumber);
        const engine = this._client.getEngine();

        return historyEntry.getSpin(engine).getWin();
    }

    public getReelSet(): ReelSet {
        return this._client.getEngine().getReelSet(this._spin.reelSetId);
    }

    public getReelsetByID(reelsetID: number): ReelSet {
        return this._client.getEngine().getReelSet(reelsetID);
    }

    public getCurrentReelsSymbols(): number[][] {
        return this._spin.symbolIds;
    }

    public getNextReelSet(): ReelSet {
        if (this.nextAction === Action.SPIN) {
            return this._client.getEngine().getMainGameReelSet(this._client.getSession());
        } else if (this.nextAction === Action.FREESPIN) {
            return this._client.getEngine().getBonusEngine(Bonus.FREE_GAMES_BONUS).getBonusReelSet(this._client.getSession());
        } else if (this.nextAction === Action.RESPIN) {
            return this._client.getEngine().getBonusEngine(Bonus.RESPIN_BONUS).getBonusReelSet(this._client.getSession());
        } else if (this.nextAction === Action.PICK) {
            return this._client.getEngine().getMainGameReelSet(this._client.getSession());
        } else if (this.nextAction === Action.SELECT) {
            return this._client.getEngine().getMainGameReelSet(this._client.getSession());
            // @ts-ignore TODO: refactoring required Action.MINISPIN not exists
        } else if (this.nextAction === Action.MINISPIN) {
            // @ts-ignore TODO: refactoring required Bonus.MINI_GAME_BONUS not exists
            return this._client.getEngine().getBonusEngine(Bonus.MINI_GAME_BONUS).getBonusReelSet(this._client.getSession());
        }

        throw new Error('unimplemented!');
    }

    public getMainGameReelSet(): ReelSet {
        return this._client.getEngine().getMainGameReelSet(this._client.getSession());
    }

    public getWin(): number {
        return this._spin?.getWin();
    }

    public getTotalWin(): number {
        if (this._client.getSession().getTotalWin() === 0 && this._spin) {
            return this._spin.getWin();
        }

        return this._client.getSession().getTotalWin();
    }

    public getSessionBet(): number {
        return this._client.getSession().getTotalBet().bet;
    }

    public getBalance(): number {
        return this._clientWrapper.getBalance();
    }

    public getSlotDimension(): number[] {
        return this._client.getEngine().getDimension();
    }

    public getBet(): number {
        return this.getBets()[this._betId];
    }

    public getWager(): number {
        return this._wager;
    }

    public getTotalBet(): number {
        return this.getBet() * this._wager;
    }

    public getTotalSessionBet(): number {
        return this.getBuyFeature().isRequest ? this.getBuyFeaturePrice() : this.getTotalBet();
    }

    public getBetFactor(symbolId: string | number): number {
        return this._totalBetSymbolIndexes.includes(Number(symbolId)) ? this.getTotalBet() : this.getBet();
    }

    public getSymbolIds(): number[][] {
        return this.spinData.symbolIds;
    }

    private setSettingsAfterInitAction(): void {
        const bets = this.getBets().map(value => value * this.getWager() / this._currencyFormatter.fractionSize);
        const minBet = Math.min(...bets);
        const maxBet = Math.max(...bets);

        this.settings.set('minBet', minBet);
        this.settings.set('maxBet', maxBet);
    }

    public spin(cb: Function): void {
        if (this.hasActiveAwardedFeature()) {
            this.acquireAwardedFeature(cb);

            return;
        }

        this._callback = cb;

        const spinRequest = () => {
            this.previousSpinRoundWin = 0;
            this.previousAction = this.nextAction;
            this._eventManager.emit(LogicEvents.ROUND_START, this.getTotalBet());
            this.emitRoundStarted();
            this._clientWrapper.spin(this.getBet());
            this.getBuyFeature().isProgressStarted = false;
        };

        this._browser.addTimeout(() => {
            if (this.getPromotion().isActive()) {
                this.considerIndependentBet(this.getBet(), spinRequest);
            } else {
                spinRequest();
            }
        }, 0);
    }

    public freespin(cb: Function): void {
        this._callback = cb;

        this._updateManager.add(new Timer(() => {
            this.previousSpinRoundWin = this.getTotalWin();
            this.previousAction = this.nextAction;
            this._retriggerCount = this.getFreeGamesTotalCount();
            this._eventManager.emit(LogicEvents.ROUND_START, 0);
            this.emitRoundStarted();
            this._clientWrapper.freeSpin();
        }, 0, true));
    }

    public progressiveValues(cb: Function): void {
        this._callback = cb;

        this._updateManager.add(new Timer(() => {
            this._clientWrapper.progressiveValues();
        }, 0, true));
    }

    public onSpin(spin: Spin): void {
        this.spinData = spin;
        this.nextAction = this._client.getSession().nextAction();
        this.processSpin();
        this._callback && this._callback();
        this._eventManager.emit(LogicEvents.SPIN_RESPONSE);
    }

    public processSpin(): void {
        this.processSpinHyperSpin();
    }

    public processFreeSpin(): void {
        this.processFreeSpinHyperSpin();
    }

    public processRespin(): void {
        this.processRespinHyperSpin();
    }

    public processMiniSpin(): void {
        this.processMiniSpinHyperSpin();
    }

    public onFreeSpin(freeSpin: Spin): void {
        this.spinData = freeSpin;
        this._retriggerCount = this.getFreeGamesTotalCount() - this._retriggerCount;
        this.nextAction = this._client.getSession().nextAction();
        this.processFreeSpin();
        this._callback && this._callback();
        this._eventManager.emit(LogicEvents.FREE_SPIN_RESPONSE);
    }

    public onRespin(respin: Spin): void {
        this.spinData = respin;
        this.nextAction = this._client.getSession().nextAction();
        this.processRespin();
        this._callback && this._callback();
        this._eventManager.emit(LogicEvents.RESPIN_RESPONSE);
    }

    public onMiniSpin(spin: Spin): void {
        this.spinData = spin;
        this.nextAction = this._client.getSession().nextAction();
        this.processMiniSpin();
        this._callback && this._callback();
        this._eventManager.emit(LogicEvents.MINI_SPIN_RESPONSE);
    }

    public onProgressiveValues(): void {
        this._eventManager.emit(LogicEvents.PROGRESSIVE_RESPONSE);
    }

    public onPick(): void {
        this.nextAction = this._client.getSession().nextAction();
        this._callback && this._callback();
        this._eventManager.emit(LogicEvents.PICK_RESPONSE);
        strictUiEmitter.emit('LogicEvents.PICK_RESPONSE');
    }

    public onSelect(): void {
        this.nextAction = this._client.getSession().nextAction();
        this._callback && this._callback();
        this._eventManager.emit(LogicEvents.SELECT_RESPONSE);
    }

    public onBuy(index: number): void {
        this.nextAction = this._client.getSession().nextAction();
        this._callback && this._callback();
        this._eventManager.emit(LogicEvents.BUY_RESPONSE, index);
    }

    public onBuyFeature(spin: Spin): void {
        this.spinData = spin;
        this.nextAction = this._client.getSession().nextAction();
        this.processSpin();
        this._callback && this._callback();
        this._eventManager.emit(LogicEvents.SPIN_RESPONSE);
        this.getBuyFeature().setProgressStarted();
    }

    public hasActiveAwardedFeature(): boolean {
        return this._clientWrapper.getPromotion().isActive() &&
            this._clientWrapper.getPromotion().getQualifier() === 'awarded-feature';
    }

    protected emitRoundStarted(): void {
        this._eventManager.emit(LogicEvents.ROUND_STARTED, {
            balanceBefore: this.getBalance()
        });
    }

    protected createArray(length: number): number[] {
        const res = [];

        for (let i = 0; i < length; i++) {
            res[i] = i;
        }

        return res;
    }

    protected getSelectBonus(): SelectBonus {
        return this._client.getSession().getBonus(Bonus.SELECT_BONUS);
    }

    protected getPickBonus(): PickBonus {
        return this._client.getSession().getBonus(Bonus.PICK_BONUS);
    }

    protected getFirecatcherBonus(): FirecatcherRespinBonus {
        return this._client.getSession().getBonus(Bonus.RESPIN_BONUS) as FirecatcherRespinBonus;
    }

    protected processSpinHyperSpin(): void {
        // this._hyperSpin = HyperSpinUtils.createEmpty(this.spin);
    }

    protected processFreeSpinHyperSpin(): void {
        this.processSpinHyperSpin();
    }

    protected processRespinHyperSpin(): void {
        this.processSpinHyperSpin();
    }

    protected processMiniSpinHyperSpin(): void {
        this.processSpinHyperSpin();
    }

    protected getRestoredSpinData(): Spin {
        const actionNumber = this.getActionNumberForLastSpin();
        const historyEntry: GameHistoryEntry = this._client.getGameHistory().getEntry(actionNumber);

        return historyEntry.getSpin(this._client.getEngine());
    }

    protected getActionNumberForLastSpin(): number {
        let actionNumber;

        // order does matter - freespins should be on last place (because we can have another bonus in freespins)
        // for respin and fs we get last spin, for select and pick we get spin that trigger bonus
        if (this.getCurrentBonus(Bonus.PICK_BONUS)) {
            actionNumber = this.getCurrentBonus(Bonus.PICK_BONUS).getActionNumber();
        } else if (this.getCurrentBonus(Bonus.SELECT_BONUS)) {
            actionNumber = this.getCurrentBonus(Bonus.SELECT_BONUS).getActionNumber();
        } else if (this.getCurrentBonus(Bonus.RESPIN_BONUS)) {
            actionNumber = this.getActionNumber() - 1;
        } else if (this.getCurrentBonus(Bonus.FREE_GAMES_BONUS)) {
            actionNumber = this.getActionNumber() - 1;
        }

        return actionNumber;
    }

    public getFreeGamesBonus(): FreeGamesBonus {
        return this._client.getSession().getBonus(Bonus.FREE_GAMES_BONUS);
    }

    public pick(cb: Function, pickId: number): void {
        this._callback = cb;

        this._browser.addTimeout(() => {
            this.previousSpinRoundWin = this.getTotalWin();
            this.previousAction = this.nextAction;
            this._clientWrapper.pick(pickId);
        }, 0);

    }

    public select(cb: Function, pickId: number): void {
        this._callback = cb;

        this._updateManager.add(new Timer(() => {
            this.previousSpinRoundWin = this.getTotalWin();
            this.previousAction = this.nextAction;
            this._clientWrapper.select(pickId);
        }, 0, true));
    }

    public respin(cb: Function): void {
        this._callback = cb;

        this._browser.addTimeout(() => {
            this.previousSpinRoundWin = this.getTotalWin();
            this.previousAction = this.nextAction;
            this.emitRoundStarted();
            this._eventManager.emit(LogicEvents.ROUND_START, 0);
            this._clientWrapper.respin();
        }, 0);
    }

    public minispin(cb: Function): void {
        this._callback = cb;

        this._browser.addTimeout(() => {
            this.previousSpinRoundWin = this.getTotalWin();
            this.previousAction = this.nextAction;
            this.emitRoundStarted();
            this._eventManager.emit(LogicEvents.ROUND_START, 0);
            this._clientWrapper.minispin();
        }, 0);
    }

    public buy(cb: Function): void {
        this._callback = cb;

        this._browser.addTimeout(() => {
            this.previousSpinRoundWin = 0;
            this.previousAction = this.nextAction;
            this.emitRoundStarted();
            this._eventManager.emit(LogicEvents.ROUND_START, this.getTotalBet());
            this._clientWrapper.buy(this.getBet());
        }, 0);
    }

    public buyFeature(cb: Function): void {
        this._callback = cb;

        this.getBuyFeature().isRequest = true;
        this._browser.addTimeout(() => {
            this.previousSpinRoundWin = 0;
            this.previousAction = this.nextAction;
            this._eventManager.emit(LogicEvents.ROUND_START, this.getBuyFeaturePrice(), true);
            this.emitRoundStarted();
            const buyFeatureBet = this.getBuyFeature().getIndependentBet(this.getBet());

            this._clientWrapper.buyFeature(buyFeatureBet);
        }, 0);
    }

    public acquireAwardedFeature(cb: Function): void {
        this._callback = cb;
        this.getBuyFeature().isRequest = true;
        this._browser.addTimeout(() => {
            const bet = this._clientWrapper.getPromotion().getBet();

            this.considerIndependentBet(bet, () => {
                const awardedFeaturePrice = this.getBuyFeaturePrice();

                // will get reduced by the math
                this._clientWrapper.getSession().getPlayer().addBalance(awardedFeaturePrice);
                this.previousSpinRoundWin = 0;
                this.previousAction = this.nextAction;
                this._eventManager.emit(LogicEvents.ROUND_START, awardedFeaturePrice);
                this.emitRoundStarted();
                this._clientWrapper.buyFeature(bet);
            });
        }, 0);
    }

    protected considerIndependentBet(actionsBet: number, actionsRequest: Function): void {
        const originalBetList = this._clientWrapper.getSession().getGameSettings().bets;
        const isIndependendBet = !originalBetList.includes(actionsBet);

        if (isIndependendBet) {
            const betsWithIndependentBet = [...originalBetList, actionsBet].sort((a, b) => a - b);

            this._client.getSession().getGameSettings().bets = betsWithIndependentBet;
        }

        actionsRequest();

        if (isIndependendBet) {
            this._client.getSession().getGameSettings().bets = originalBetList;
        }
    }

    public onResponse(message: ServerMessage): void {
        this.getBuyFeature().isRequest = false;

        switch (message.topic) {
            case ServerMessage.TOPIC_GAMESERVER: {
                this.handleTopicGameServer(message);
                break;
            }
            case ServerMessage.TOPIC_CONNECTION_TIMEOUT: {
                this._eventManager.emit(LogicEvents.CONNECTION_TIMEOUT, message.data);
                break;
            }
            case ServerMessage.TOPIC_CONNECTION_SLOW: {
                this._eventManager.emit(LogicEvents.CONNECTION_SLOW, message.data);
                break;
            }
            case ServerMessage.TOPIC_GAMESERVER_ERROR: {
                this._eventManager.emit(LogicEvents.GAMESERVER_ERROR, message.data);
                break;
            }
            default:
                this._clientWrapper.responseHandle(message);

                return;
        }
    }

    protected handleTopicGameServer(message: ServerMessage): void {
        const msg: Message = message.data;
        const action = msg.getAction();
        const spin: Spin | undefined = msg.getField(Field.SPIN) ?? undefined;
        const value: number = msg.getField(Field.VALUE) ?? undefined;
        const positions = spin?.reelPositions?.join(',');

        consoleLogger
            .logData('⭮ Action', {
                positions,
                actionType: action,
                actionId: this.getActionNumber(),
                value
            })
            .write('Logic:', this);

        switch (action) {
            case Action.INIT:
                this.onInit();
                break;
            case Action.SPIN:
                this.onSpin(spin!);
                break;
            case Action.FREESPIN:
                this.onFreeSpin(spin!);
                break;
            case Action.RESPIN:
                this.onRespin(spin!);
                break;
            // @ts-ignore TODO: refactoring required Action.MINISPIN not exists
            case Action.MINISPIN:
                this.onMiniSpin(spin!);
                break;
            // @ts-ignore
            case Action.BUY:
                this.onBuy(value);
                break;
            case Action.SELECT:
                this.onSelect();
                break;
            case Action.PICK:
                this.onPick();
                break;
            case Action.PROGRESSIVE_VALUES:
                this.onProgressiveValues();
                break;
            // @ts-ignore
            case Action.BUY_FEATURE:
                this.onBuyFeature(spin!);
                break;
            default:
                consoleLogger.warn(`Cannot handle message ${msg.getAction()}`);
        }

        const responseEventData = {
            nextActionIsSpin: this.nextAction === Action.SPIN,
            previousActionIsSpin: this.previousAction === Action.SPIN,
            actionType: action,
            actionId: this.getActionNumber()
        };

        this._eventManager.emit(LogicEvents.RESPONSE, responseEventData);
        this._eventManager.emitAsync(LogicEvents.RESPONSE, responseEventData);
    }

    public onInit(): void {
        this._wager = this._client.getEngine().getMaxWager();

        if (this._client.getSession().isFinished()) {
            const settingsBets = this.settings.get('bets');

            if (settingsBets && settingsBets.length > 0) {
                this._client.getSession().getGameSettings().bets = settingsBets;
            }

            this._client.getSession().setTotalBet(new TotalBet(this.getBet(), this._wager));
        } else {
            this.onRestore();
        }
        this.previousAction = Action.SPIN;
        this.nextAction = this._client.getSession().nextAction();
        this.setSettingsAfterInitAction();
        this._callback!();
        this._eventManager.emit(LogicEvents.INIT_RESPONSE);
    }

    public onRestore(): void {
        this.spinData = this.getRestoredSpinData();
        this.getBuyFeature().setProgressStarted();
    }

    public getActionNumber(): number {
        const an = this._client.getSession().getActionNumber();
        const reComputeLogicTrigger = slotStore.reComputeLogicTrigger.value + 1;

        slotStore.batchUpdate({reComputeLogicTrigger, actionNumber: an});

        return an;
    }

    public getSession(totalActions: number): number[] {
        const firstAction = this.getActionNumber() - totalActions;

        return this._client.getGameHistory().prepareTestSession(firstAction, totalActions);
    }

    public getSessionFromLocalStorage(): number[] | undefined {
        try {
            const key = '__some_random_key_you_are_not_going_to_use__';

            storage.setItem(key, key);
            storage.removeItem(key);
        } catch (e) {
            return undefined;
        }

        const currentSession = storage.getItem('currentSession');

        return currentSession && JSON.parse(currentSession).session.map((item: any) => parseInt(item, 10));
    }

    public getPromotion<PromotionLogic extends IPromotionLogic>(): PromotionLogic {
        return this._promotionLogic as PromotionLogic;
    }

    public getBuyFeature(): IBuyFeatureLogic<BuyFeatureT> {
        return this._buyFeatureLogic;
    }

    private createBuyFeatureLogicWrapper(): void {
        this._buyFeatureLogic = new BuyFeatureLogicWrapper<GenericSession, GenericEngine, BuyFeatureT>(this._client, this.getPromotion());
    }

    public isAvailableNextAction(): boolean {
        return !this.getBuyFeature().isRequest;
    }

    public getBuyFeatureMultiplier(): number {
        return this.getBuyFeature().getMultiplier();
    }

    public getBuyFeaturePrice(): number {
        return this.getBuyFeature().getPrice(this.getBet());
    }

    public getPromotionPrice(qualifier: PromotionQualifier, bet: number): number {
        if (qualifier === 'awarded-feature') {
            return this.getBuyFeature().getPrice(bet);
        } else {
            return bet * this._wager;
        }
    }

    public canBuyFeature(): boolean {
        const maxBuyFeatureBet = this.settings.get('maxBuyFeatureBet')!;

        return this.getBet() <= maxBuyFeatureBet;
    }

    public getBuyFeatureOrUsualBonus(bonusId: number): number {
        return this.getBuyFeature().getBuyFeatureOrUsualBonus(bonusId);
    }

    public shouldHideBuyFeature(): boolean {
        return this.getBuyFeature().shouldHide();
    }

    public shouldShowBuyFeatureDescription(): boolean {
        const isBuyFeatureAvailable = this.settings.get('buyFeatureAvailable') ?? false;
        const featureExists = this.getBuyFeature().isBuyFeatureGame();
        const isBetWithinLimit = !this.getBuyFeature().isMinBetAboveBuyFeatureLimit();

        return isBuyFeatureAvailable && featureExists && isBetWithinLimit;
    }

    public canSpin(bet: number): boolean {
        return this._clientWrapper.canSpin(bet);
    }

    public getPaysByStep(collapseStepID = slotStore.logic_collapseStepID.peek()): Pay[] {
        if (this.getTumbleSpin().getByStepPays) {
            return this.getTumbleSpin().getByStepPays(collapseStepID);
        }

        return [];
    }

    public getByStepSymbolIds(collapseStepID = slotStore.logic_collapseStepID.peek()): number[][] {
        if (this.getTumbleSpin().getByStepSymbolIds?.(collapseStepID)) {
            return this.getTumbleSpin().getByStepSymbolIds(collapseStepID);
        }

        return [];
    }

    public getByStepDropAmount(collapseStepID = slotStore.logic_collapseStepID.peek()): number[] {
        if (this.getTumbleSpin().getByStepDropAmount?.(collapseStepID)) {
            return this.getTumbleSpin().getByStepDropAmount(collapseStepID);
        }

        return [];
    }

    public getByStepReelPositions(collapseStepID = slotStore.logic_collapseStepID.peek()): number[] {
        if (this.getTumbleSpin().getByStepReelPositions?.(collapseStepID)) {
            return this.getTumbleSpin().getByStepReelPositions(collapseStepID);
        }

        return [];
    }

    public getByStepBeforeDropSymbolIds(collapseStepID = slotStore.logic_collapseStepID.peek()): number[][] {
        if (this.getTumbleSpin().getByStepBeforeDropSymbolIds?.(collapseStepID)) {
            return this.getTumbleSpin().getByStepBeforeDropSymbolIds(collapseStepID);
        }

        return [];
    }

    public getLastStepSymbolIds(): number[][] {
        if (this.getTumbleSpin().getLastStepSymbolIds) {
            return this.getTumbleSpin().getLastStepSymbolIds();
        }

        return [];
    }

    public getAllPays(): PayEntries {
        if (this.getTumbleSpin().getAllPays) {
            return this.getTumbleSpin().getAllPays();
        }

        return {} as PayEntries;
    }

    public isAnyCollapseOnSpin(): boolean {
        let isAny = false;

        if (this.previousAction === Action.SPIN || this.previousAction === Action.FREESPIN) {
            this.iterateTumbleSpinPays(pays => {
                if (!isAny) {
                    isAny = pays.filter(pay => pay.type < PayType.SCATTER).length > 0;
                }
            });
        }

        return isAny;
    }

    public isAnyScatterPayOnTumbleSpin(): boolean {
        let isAny = false;

        this.iterateTumbleSpinPays(pays => {
            if (!isAny) {
                isAny = pays.filter(pay => pay.type === PayType.SCATTER && pay.totalWin()).length > 0;
            }
        });

        return isAny;
    }

    public iterateTumbleSpinPays(callBack: (pays: Pay[]) => void, spin?: TumbleSpin): void {
        (spin ? spin : this.getTumbleSpin()).getAllPays().entries.forEach((data: {
            value: Pay[];
        }) => callBack(data.value));
    }

    public getTumbleSpin(): TumbleSpin  {
        return this.spinData as unknown as TumbleSpin;
    }

    public get spinData(): Spin {
        return this._spin;
    }

    public set spinData(spin: Spin) {
        this._spin = spin;
        slotStore.batchUpdate({logic_spinData: spin});
    }

    public get reelPositions(): number[] {
        return this._spin.reelPositions;
    }

    public get nextAction(): Action {
        return this._nextAction;
    }

    public set nextAction(action: Action) {
        this._nextAction = action;
        slotStore.batchUpdate({logic_nextAction: action});
    }

    public get hyperSpin(): number[] {
        return this._hyperSpin;
    }

    public get betId(): number {
        return this._betId;
    }

    public set betId(value: number) {
        if (this._betId !== value) {
            this._betId = value;
            slotStore.batchUpdate({betId: value, logic_buyFeatureBet: this.getBet()});

            this._eventManager.emit(LogicEvents.BET_CHANGED, this.getBetInfo());
        }
    }

    public get previousAction(): Action {
        return this._previousAction;
    }

    public set previousAction(action: Action) {
        this._previousAction = action;
        slotStore.batchUpdate({logic_previousAction: action});
    }

    public get previousSpinRoundWin(): number {
        return this._previousSpinRoundWin;
    }

    public set previousSpinRoundWin(win: number) {
        this._previousSpinRoundWin = win;
        slotStore.batchUpdate({logic_previousSpinRoundWin: win});
    }

    /**
     * BUY BONUS
     */

    public isCoinFlightFeature(symbolId: number): boolean {
        const spin = this.spinData;

        return spin.symbolIds[spin.symbolIds.length - 1].indexOf(symbolId) !== -1;
    }

    public getBonusPrice(): number {
        // @ts-ignore
        return this._client.getSession().getBonusPrice();
    }

    /**
     * <p>To get RubyCoins currency use formula:</p>
     * <b>RubyCoinsCurrency = 4 * Stake * RubyCoins / 100</b>
     * @return {number}
     */

    public getRealRubyCurrency(): number {
        // @ts-ignore
        return this._client.getSession().getRubyCoinsData().getRubyCurrency();
    }

    /**
     * To get a count of RubyCoins that was spent to Buy Bonus:
     * <b>RubyCoinsCurrency = bonusPrice / Stake / 4 </b>
     * @return {number}
     */
    public getRubyCoinsToSpend(): number {
        const session = this._client.getSession();

        return session
            // @ts-ignore
            .getRubyCoinsData()
            .getRubyCoinsToSpend(this.getBonusPrice() - this._client.getSession().getTotalBet().value());
    }

    public isBonusCanBeBought(): boolean {
        // @ts-ignore
        const bonusPrize = this._client.getSession().getBonusPrice() - this._client.getSession().getTotalBet().value();
        // @ts-ignore
        const rubyCoinsToSpend = this._client.getSession().getRubyCoinsData().getRubyCoinsToSpend(bonusPrize);

        return rubyCoinsToSpend <= this.getRubyBalance() && this.getTotalBet() <= this.getBalance();
    }

    /**
     * @return {number} player rubyBalance in player currency
     */

    public getRubyBalance(): number {
        // @ts-ignore
        return this._client.getSession().getRubyCoinsData().getRubyBalance();
    }

    /**
     * @return {number} operator min bet
     */

    public getLcdStake(): number {
        // @ts-ignore
        return this._client.getSession().getRubyCoinsData().getLcdStake();
    }

    /**
     * BUY BONUS FINISHED
     */

    public getCurrentReelsStrips(): number[][] {
        const currentReelsStrips: number[][] = [];
        const reels = this.getNextReelSet().getReels();

        reels.forEach(strip => currentReelsStrips.push(strip.getSymbols()));

        return currentReelsStrips;
    }

    /**
     * Returns array in which length is equal to number of reels and every
     * array element is a number of symbols in indexed reel.
     */
    public getBaseGameReelsMatrixSize(): number[] {
        return this._client.getEngine().getDimension();
    }

    public getFinalSpinStopPositions(): number[] {
        return this._client.getSession().getRandomValuesList().getUsedValues();
    }

    public getRoundInfo(): RoundInfo {
        const {previousSpinRoundWin} = this;
        const totalWin = this.getTotalWin();
        const win = totalWin - previousSpinRoundWin;

        return {
            win,
            totalWin,
            isBigWin: this.isBigTotalWin()
        };
    }

    public getBetInfo(): BetInfo {
        return {
            betIndex: this._betId,
            bet: this.getBet(),
            totalBet: this.getTotalBet()
        };
    }

    protected createProxy(): GameClientProxy<GenericSession, GenericEngine> {
        throw new Error('unimplemented!');
    };

    protected createdStub(): GameClientStub<GenericSession, GenericEngine> {
        throw new Error('unimplemented!');
    };

    private createGameClientProxy(createStub: boolean):
        GameClientProxy<GenericSession, GenericEngine> | GameClientStub<GenericSession, GenericEngine> {
        /* demo-block:start */
        if (createStub) {
            /* demo-block:end */
            return this.createdStub();
            /* demo-block:start */
        } else {
            return this.createProxy();
        }
        /* demo-block:end */
    }
}
